import React, { useState, useRef, useCallback } from 'react';
import { Upload, Loader2, Camera, ArrowRight, AlertTriangle, Check, RefreshCw, X } from 'lucide-react';
import { apiUpload } from '../data/api';
import t from '../i18n/it';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

function EvBadge({ value }) {
  if (value === null || value === undefined) return <span className="text-zinc-600">—</span>;
  const positive = value > 0;
  return (
    <span
      data-testid="ev-badge"
      className={`px-1.5 py-0.5 rounded text-xs font-bold ${
        positive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-800 text-zinc-500'
      }`}
      style={mono}
    >
      {positive ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
}

function OddCell({ odd, label }) {
  if (!odd) return <span className="text-zinc-600">—</span>;
  return (
    <span className="text-sm text-zinc-200" style={mono}>{odd.toFixed(2)}</span>
  );
}

export default function ScreenshotCompare() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = useCallback((f) => {
    if (!f || !f.type.startsWith('image/')) {
      setError('Seleziona un file immagine (PNG, JPEG, WEBP)');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('Immagine troppo grande (max 10MB)');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setError(null);
    setResult(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiUpload('/tt/screenshot-compare', file);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-5 fade-in max-w-4xl" data-testid="screenshot-compare-page">
      <div>
        <h1 className="text-4xl font-bold text-zinc-50 tracking-tight" style={heading}>
          {t.screenshot.title}
        </h1>
        <p className="text-zinc-400 mt-1 text-sm">{t.screenshot.subtitle}</p>
      </div>

      {/* Upload Area */}
      {!result && (
        <div className="rounded-sm overflow-hidden" style={card}>
          <div className="px-5 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid #27272A' }}>
            <div className="w-7 h-7 rounded-sm bg-emerald-500/10 flex items-center justify-center">
              <Camera size={13} className="text-emerald-400" />
            </div>
            <div>
              <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.screenshot.upload_title}</h2>
              <p className="text-xs text-zinc-500 mt-0.5">{t.screenshot.upload_desc}</p>
            </div>
          </div>

          <div className="p-5">
            {!preview ? (
              <div
                data-testid="screenshot-dropzone"
                className={`border-2 border-dashed rounded-sm p-12 text-center cursor-pointer transition-colors ${
                  dragOver
                    ? 'border-emerald-500 bg-emerald-500/5'
                    : 'border-zinc-700 hover:border-zinc-500'
                }`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <Upload size={32} className="mx-auto text-zinc-600 mb-3" />
                <p className="text-sm text-zinc-400">Trascina l'immagine qui o <span className="text-emerald-400 underline">sfoglia</span></p>
                <p className="text-xs text-zinc-600 mt-1">{t.screenshot.upload_formats}</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="screenshot-file-input"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative rounded-sm overflow-hidden" style={{ border: '1px solid #27272A', maxHeight: '300px' }}>
                  <img
                    src={preview}
                    alt="Screenshot"
                    className="w-full object-contain"
                    style={{ maxHeight: '300px' }}
                    data-testid="screenshot-preview"
                  />
                  <button
                    onClick={handleReset}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center hover:bg-black/90 transition-colors"
                    data-testid="screenshot-remove-btn"
                  >
                    <X size={14} className="text-zinc-300" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    data-testid="screenshot-analyze-btn"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-sm font-medium text-sm transition-colors disabled:opacity-50"
                    style={{ background: '#10B981', color: '#000' }}
                  >
                    {loading ? (
                      <><Loader2 size={15} className="animate-spin" /> {t.screenshot.analyzing}</>
                    ) : (
                      <><ArrowRight size={15} /> Analizza e Confronta</>
                    )}
                  </button>
                  {loading && (
                    <span className="text-xs text-zinc-500">{t.screenshot.analyzing_desc}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-sm p-4 text-xs flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="screenshot-error">
          <AlertTriangle size={13} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-sm p-4" style={card}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t.screenshot.bookmaker}</div>
              <div className="text-sm font-bold text-zinc-200 mt-1" data-testid="result-bookmaker">{result.bookmaker_name}</div>
            </div>
            <div className="rounded-sm p-4" style={card}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t.screenshot.extracted}</div>
              <div className="text-lg font-bold text-zinc-200 mt-1" style={mono} data-testid="result-extracted">{result.total_extracted}</div>
            </div>
            <div className="rounded-sm p-4" style={card}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t.screenshot.matched}</div>
              <div className="text-lg font-bold text-zinc-200 mt-1" style={mono} data-testid="result-matched">{result.total_matched}</div>
            </div>
            <div className="rounded-sm p-4" style={card}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t.screenshot.with_value}</div>
              <div className="text-lg font-bold text-emerald-400 mt-1" style={mono} data-testid="result-value-count">{result.total_with_value}</div>
            </div>
          </div>

          {/* Comparisons */}
          {result.comparisons.length > 0 && (
            <div className="space-y-3">
              {result.comparisons.map((c, i) => {
                const hasMwOdds = c.adm_odd_p1 != null || c.adm_odd_p2 != null;
                const hasOu = c.over_under && c.over_under.length > 0;

                return (
                  <div key={i} className="rounded-sm overflow-hidden" style={card} data-testid={`comparison-row-${i}`}>
                    {/* Match Header */}
                    <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #27272A' }}>
                      <div>
                        <span className="text-sm font-medium text-zinc-200">{c.db_player1}</span>
                        <span className="text-zinc-500 mx-2">vs</span>
                        <span className="text-sm font-medium text-zinc-200">{c.db_player2}</span>
                        <span className="text-[10px] text-zinc-600 ml-3">{c.tournament}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.confidence != null && (
                          <span className="text-[10px] text-zinc-500">Conf. <span style={mono}>{(c.confidence * 100).toFixed(0)}%</span></span>
                        )}
                        {c.has_value_adm ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-400" data-testid="value-badge">VALORE</span>
                        ) : (
                          <span className="text-[10px] text-zinc-600">No Value</span>
                        )}
                      </div>
                    </div>

                    <div className="p-4 space-y-3">
                      {/* Match Winner Row */}
                      {hasMwOdds && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Match Winner</div>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { label: c.db_player1, adm: c.adm_odd_p1, bk: c.betsapi_odd_p1, fair: c.fair_odd_p1, ev: c.ev_p1_adm, edge: c.edge_p1_adm },
                              { label: c.db_player2, adm: c.adm_odd_p2, bk: c.betsapi_odd_p2, fair: c.fair_odd_p2, ev: c.ev_p2_adm, edge: c.edge_p2_adm },
                            ].map((p, j) => (
                              <div key={j} className={`rounded-sm p-3 ${p.ev > 0 ? 'bg-emerald-500/[0.06] border border-emerald-500/20' : 'bg-zinc-900/50'}`}>
                                <div className="text-xs text-zinc-400 mb-2 truncate">{p.label}</div>
                                <div className="flex items-center gap-3 flex-wrap">
                                  <div>
                                    <div className="text-[9px] text-zinc-600">ADM</div>
                                    <div className={`text-sm font-bold ${p.ev > 0 ? 'text-emerald-400' : 'text-zinc-300'}`} style={mono}>
                                      {p.adm?.toFixed(2) ?? '—'}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[9px] text-zinc-600">Fair</div>
                                    <div className="text-sm text-zinc-400" style={mono}>{p.fair?.toFixed(2) ?? '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-[9px] text-zinc-600">BetsAPI</div>
                                    <div className="text-sm text-zinc-500" style={mono}>{p.bk?.toFixed(2) ?? '—'}</div>
                                  </div>
                                  <div className="ml-auto">
                                    <div className="text-[9px] text-zinc-600">EV</div>
                                    <EvBadge value={p.ev} />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Over/Under Rows */}
                      {hasOu && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Over/Under Punti</div>
                          {c.over_under.map((ou, k) => (
                            <div key={k} className="grid grid-cols-2 gap-2 mb-2" data-testid={`ou-line-${ou.line}`}>
                              {[
                                { label: `Over ${ou.line}`, adm: ou.adm_over, fair: ou.fair_over, ev: ou.ev_over_adm, prob: ou.prob_over },
                                { label: `Under ${ou.line}`, adm: ou.adm_under, fair: ou.fair_under, ev: ou.ev_under_adm, prob: ou.prob_under },
                              ].map((side, j) => (
                                <div key={j} className={`rounded-sm p-3 ${side.ev > 0 ? 'bg-emerald-500/[0.06] border border-emerald-500/20' : 'bg-zinc-900/50'}`}>
                                  <div className="text-xs text-zinc-400 mb-2">{side.label}</div>
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <div>
                                      <div className="text-[9px] text-zinc-600">ADM</div>
                                      <div className={`text-sm font-bold ${side.ev > 0 ? 'text-emerald-400' : 'text-zinc-300'}`} style={mono}>
                                        {side.adm?.toFixed(2) ?? '—'}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-[9px] text-zinc-600">Fair</div>
                                      <div className="text-sm text-zinc-400" style={mono}>{side.fair?.toFixed(2) ?? '—'}</div>
                                    </div>
                                    <div>
                                      <div className="text-[9px] text-zinc-600">Prob</div>
                                      <div className="text-sm text-zinc-400" style={mono}>{side.prob != null ? `${side.prob}%` : '—'}</div>
                                    </div>
                                    <div className="ml-auto">
                                      <div className="text-[9px] text-zinc-600">EV</div>
                                      <EvBadge value={side.ev} />
                                    </div>
                                  </div>
                                  {ou.expected_total && j === 0 && (
                                    <div className="text-[9px] text-zinc-600 mt-1.5">
                                      Media attesa: <span className="text-zinc-400" style={mono}>{ou.expected_total.toFixed(1)} pts</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* No matches found */}
          {result.total_extracted === 0 && (
            <div className="rounded-sm p-6 text-center text-sm text-zinc-500" style={card} data-testid="no-matches-msg">
              {t.screenshot.no_matches}
            </div>
          )}

          {result.total_extracted > 0 && result.total_matched === 0 && (
            <div className="rounded-sm p-6 text-center text-sm text-zinc-500" style={card} data-testid="no-db-match-msg">
              {t.screenshot.no_db_match}
            </div>
          )}

          {/* Unmatched */}
          {result.unmatched?.length > 0 && (
            <div className="rounded-sm overflow-hidden" style={card}>
              <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid #27272A' }}>
                <AlertTriangle size={13} className="text-amber-500" />
                <h3 className="text-xs font-medium text-zinc-400">{t.screenshot.unmatched_title}</h3>
                <span className="text-xs text-zinc-600">({result.unmatched.length})</span>
              </div>
              <div className="divide-y" style={{ borderColor: '#1F1F22' }}>
                {result.unmatched.map((u, i) => (
                  <div key={i} className="px-5 py-2.5 flex items-center justify-between text-xs" data-testid={`unmatched-row-${i}`}>
                    <span className="text-zinc-500">{u.ocr_player1} vs {u.ocr_player2}</span>
                    <span className="text-zinc-600" style={mono}>
                      {u.odd_player1?.toFixed(2) ?? '—'} / {u.odd_player2?.toFixed(2) ?? '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Another */}
          <button
            onClick={handleReset}
            data-testid="upload-another-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <RefreshCw size={14} /> {t.screenshot.upload_another}
          </button>
        </div>
      )}
    </div>
  );
}
